import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useChangePassword, useUpdateMe } from '@/api/auth';
import { ApiError } from '@/api/client';
import type { WorkingHours } from '@/api/types';
import { Avatar, Badge, Button, Card, Input, useToast } from '@/components/ui';
import { useAuth } from '@/app/auth/useAuth';
import { RoleBadge, WorkingHoursEditor } from './shared';

const passwordSchema = z
  .object({
    currentPassword: z.string().min(1, 'Required'),
    newPassword: z.string().min(10, 'At least 10 characters'),
    confirm: z.string(),
  })
  .refine((v) => v.newPassword === v.confirm, { message: 'Passwords don’t match', path: ['confirm'] });

export function ProfilePage() {
  const { user } = useAuth();
  const toast = useToast();
  const update = useUpdateMe();
  const [name, setName] = useState(user.name);
  const [hours, setHours] = useState<WorkingHours>(user.workingHours);

  const save = () =>
    update.mutate(
      { name: name.trim(), workingHours: hours },
      { onSuccess: () => toast.success('Profile saved'), onError: (e) => toast.error(e.message) },
    );

  return (
    <div className="space-y-6">
      <Card className="p-5 sm:p-6">
        <div className="flex items-center gap-4">
          <Avatar name={user.name} src={user.avatarUrl} status={user.status} size="lg" />
          <div className="min-w-0">
            <p className="font-heading truncate text-lg font-bold text-slate-900">{user.name}</p>
            <p className="truncate text-sm text-slate-500">{user.email}</p>
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              <RoleBadge role={user.role} />
              {user.skills.map((s) => (
                <Badge key={s}>{s}</Badge>
              ))}
            </div>
          </div>
        </div>
        <div className="mt-6 grid gap-6 md:grid-cols-2">
          <Input
            label="Display name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={120}
          />
          <div className="text-sm text-slate-600">
            <p className="font-medium text-slate-700">Capacity</p>
            <p className="mt-1.5">
              {user.maxConcurrentChats} concurrent chats · {user.maxOpenTickets} open tickets
            </p>
            <p className="mt-1 text-xs text-slate-500">Set by your admin.</p>
          </div>
          <div className="md:col-span-2">
            <WorkingHoursEditor value={hours} onChange={setHours} />
          </div>
        </div>
        <div className="mt-6 flex justify-end">
          <Button onClick={save} loading={update.isPending} disabled={!name.trim()}>
            Save profile
          </Button>
        </div>
      </Card>
      <ChangePasswordCard />
    </div>
  );
}

function ChangePasswordCard() {
  const change = useChangePassword();
  const toast = useToast();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<z.infer<typeof passwordSchema>>({ resolver: zodResolver(passwordSchema) });

  const onSubmit = handleSubmit(({ currentPassword, newPassword }) =>
    change.mutate(
      { currentPassword, newPassword },
      {
        onSuccess: () => {
          toast.success('Password changed. Please sign in again.');
          qc.clear();
          navigate('/login', { replace: true });
        },
        onError: (e) => {
          if (e instanceof ApiError && e.code === 'BAD_REQUEST')
            setError('currentPassword', { message: e.message });
          else toast.error(e.message);
        },
      },
    ),
  );

  return (
    <Card className="p-5 sm:p-6">
      <h2 className="text-base font-bold">Change password</h2>
      <p className="mt-1 text-sm text-slate-500">You’ll be signed out of all devices.</p>
      <form onSubmit={onSubmit} noValidate className="mt-4 grid gap-4 md:grid-cols-3">
        <Input
          label="Current password"
          type="password"
          autoComplete="current-password"
          error={errors.currentPassword?.message}
          {...register('currentPassword')}
        />
        <Input
          label="New password"
          type="password"
          autoComplete="new-password"
          error={errors.newPassword?.message}
          {...register('newPassword')}
        />
        <Input
          label="Confirm new password"
          type="password"
          autoComplete="new-password"
          error={errors.confirm?.message}
          {...register('confirm')}
        />
        <div className="flex justify-end md:col-span-3">
          <Button type="submit" variant="secondary" loading={change.isPending}>
            Update password
          </Button>
        </div>
      </form>
    </Card>
  );
}
